import { HAND_BUDGET, HAND_MAX, burdenOf, definition, stackLimit, twoHanded } from './mass.js';
import { NO_PERKS } from './perks.js';

/**
 * What is in your hands.
 *
 * It does not know what a round is. It knows a kind string, a count, and an
 * optional variant, and everything it can tell you about them it works out from
 * mass: how many fit, how much they slow you down, whether both hands are busy.
 * A wine bottle and a banquet chair go through this identically, and so does a
 * scorpion.
 *
 * One kind at a time, still. That is the rule the whole ferrying loop is built
 * on: an inventory that holds rounds and chairs and linens at once turns a
 * banquet setup into a single trip with a checklist at the end, and the length
 * of the service corridor stops meaning anything. It also happens to be what
 * hands are.
 *
 * Picking up a different kind at a supply point silently returns whatever you
 * were holding. You are standing in the storeroom — putting three chairs back
 * on the stack is not a decision worth a button.
 *
 * The budget is a parameter, so a cart is this object with a bigger one.
 */
export function createCarry({ budget = HAND_BUDGET, cap = HAND_MAX, perk = () => NO_PERKS.carry } = {}) {
  let kind = null;
  let count = 0;
  /**
   * Which one, for kinds that come in more than one. Only linen does, and only
   * because the BEO grades the colour — a load of linen that has forgotten what
   * colour it is cannot be marked wrong, and a spec line nothing can fail is
   * not a spec line.
   */
  let variant = null;
  /**
   * Which event, if any, has these written down against a room.
   *
   * On the load rather than on each unit, because a pair of hands holds one
   * kind at a time and a stack drawn from one shelf for one room is one stack.
   */
  let claim = null;
  const listeners = new Set();

  function emit() {
    for (const listener of listeners) listener({ kind, count, variant });
  }

  /**
   * The class perk goes on the cap and never on the budget.
   *
   * That is not a detail, it is the whole guarantee. The cap only ever binds on
   * light things — a 35kg round is held to two by the weight and would not
   * notice a cap of a hundred — so adding to it is "more small items" in the
   * only place small items are counted, and it provably cannot put a third
   * round in a pair of arms. A perk on the budget would have quietly made the
   * player stronger, which is not what any of this is for.
   */
  function limit(forKind) {
    return stackLimit(forKind, { budget, cap: cap + perk().extra });
  }

  /**
   * How much of a unit is still owed to the floor.
   *
   * Grip is a fraction rather than a coin flip, so it has to be carried
   * somewhere between bumps: a grip of 0.2 means one bump in five is survived,
   * and the only way to mean that exactly is to keep the remainder. Reset with
   * the load, because a new armful is a new grip on it.
   */
  let spillDebt = 0;

  return {
    budget,
    get kind() {
      return kind;
    },
    get count() {
      return count;
    },
    get item() {
      return kind ? definition(kind) : null;
    },
    get empty() {
      return count === 0;
    },
    get variant() {
      return variant;
    },
    get claim() {
      return claim;
    },
    set claim(value) {
      claim = value;
    },
    get capacity() {
      return kind ? limit(kind) : 0;
    },
    /** 0..1, for the movement penalty. A part load is a part penalty. */
    get burden() {
      return kind ? burdenOf(kind, count, { budget }) : 0;
    },
    /** Both hands busy: no climbing, a weaker throw. */
    get twoHanded() {
      return Boolean(kind) && twoHanded(kind);
    },

    /** What one of these would be, without taking it. Used by the UI. */
    limitFor: limit,

    onChange(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    /** Fills your hands from a supply point. Returns how many you picked up. */
    take(nextKind, nextVariant = null) {
      if (!definition(nextKind)) throw new Error(`no object kind "${nextKind}"`);
      const room = limit(nextKind);
      // Already carrying a full load of the same thing: nothing to do, and
      // saying so is better than a silent no-op. A different colour of linen is
      // not the same thing, so that still swaps.
      if (kind === nextKind && variant === nextVariant && count >= room) {
        return { ok: false, reason: 'full', count };
      }
      const returned =
        kind && (kind !== nextKind || variant !== nextVariant) ? { kind, count, variant } : null;
      kind = nextKind;
      variant = nextVariant;
      const before = returned ? 0 : count;
      count = room;
      emit();
      return { ok: true, count, variant, took: count - before, returned };
    },

    /**
     * How many more of this you could take on.
     *
     * Asked before drawing from the shelf, so the shelf is never debited for
     * units that would not have fitted anyway.
     */
    room(nextKind, nextVariant = null) {
      if (!definition(nextKind)) return 0;
      if (kind === nextKind && variant === nextVariant) return Math.max(0, limit(nextKind) - count);
      return limit(nextKind);
    },

    /**
     * Adds a counted number of one thing, returning whatever was displaced.
     *
     * The blunt `take()` fills to capacity from an infinite supply. Once the
     * storeroom can run out, the caller has to say how many it actually got.
     */
    add(nextKind, nextVariant, howMany) {
      if (!definition(nextKind) || howMany <= 0) return { ok: false, count };
      const swapping = kind && (kind !== nextKind || variant !== nextVariant);
      const returned = swapping ? { kind, count, variant } : null;
      if (swapping) count = 0;
      kind = nextKind;
      variant = nextVariant;
      count = Math.min(limit(nextKind), count + howMany);
      emit();
      return { ok: true, count, variant, returned };
    },

    /**
     * Something walked into you.
     *
     * The one thing that used to be a flat `spend()` at the call site and is
     * now a question the hands answer, because how much of a knock a load
     * survives is a fact about the hands holding it. Returns how many units
     * actually hit the floor, which is what the caller has to spawn.
     *
     * At tier zero the debt goes up by a whole unit every time, so this is the
     * old behaviour exactly: bumped, drop one. At the top tier it goes up by
     * nothing and a load never spills, which is what a fifth level ought to
     * buy.
     */
    jostle() {
      if (!count) return 0;
      spillDebt += 1 - perk().grip;
      let spilled = 0;
      /*
       * The epsilon is not decoration. A grip of 0.4 adds 0.6 five times and
       * the fifth one lands on 0.9999999999999999, so without it the top of
       * every ladder is a unit out and the perk quietly under-delivers in a way
       * that looks like a balance decision rather than like binary floating
       * point.
       */
      while (spillDebt >= 1 - 1e-9 && count > 0) {
        spillDebt -= 1;
        count -= 1;
        spilled += 1;
      }
      if (!count) {
        kind = null;
        variant = null;
        spillDebt = 0;
      }
      if (spilled) emit();
      return spilled;
    },

    /** What the hands are still owing the floor. Read by the tests. */
    get spillDebt() {
      return spillDebt;
    },

    /** Spends one. Returns false if there was nothing to spend. */
    spend() {
      if (!count) return false;
      count -= 1;
      if (!count) {
        kind = null;
        variant = null;
      }
      emit();
      return true;
    },

    /** Puts one back in your hands, if there is room. */
    gain(nextKind, nextVariant = null) {
      if (!definition(nextKind)) return false;
      if (kind && (kind !== nextKind || variant !== nextVariant)) return false;
      if (count >= limit(nextKind)) return false;
      kind = nextKind;
      variant = nextVariant;
      count += 1;
      emit();
      return true;
    },

    clear() {
      kind = null;
      count = 0;
      variant = null;
      claim = null;
      spillDebt = 0;
      emit();
    },

    toJSON: () => ({ kind, count, variant, claim }),
    load(state) {
      spillDebt = 0;
      claim = state?.claim ?? null;
      kind = state?.kind ?? null;
      count = state?.count ?? 0;
      variant = state?.variant ?? null;
    },
  };
}
